// 环境变量控制是否用本地 mock 数据
const USE_ENV_MOCK = process.env.REACT_APP_USE_ENV_MOCK === "true";
const LATEST_UTXO_RESP = process.env.REACT_APP_LATEST_UTXO_RESP;
const LATEST_LARGETX_RESP = process.env.REACT_APP_LATEST_LARGETX_RESP;

function parseEnvJson(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}
import React, { useEffect, useState } from "react";
import axios from "axios";
import OverviewChart from "./components/OverviewChart";
import BlockCards from "./components/BlockCards";
import TopAddressesPanel from "./components/TopAddressesPanel";
import { FaBitcoin, FaGithub } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { IoFlash } from "react-icons/io5";
import "./App.css";

function App() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedBlockHeight, setSelectedBlockHeight] = useState(null);

  const retryRequest = async (requestFn, maxRetries = 3, delayMs = 5000) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        return await requestFn();
      } catch (err) {
        lastError = err;
        if (attempt <= maxRetries) {
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }
    }
    throw lastError;
  };

  const parseTransactions = (transactions) => {
    if (!Array.isArray(transactions)) return [];
    return transactions.map((tx) => ({
      amountBtc: Number(tx.amount_btc ?? tx.amountBtc ?? 0),
      fromAddresses: Array.isArray(tx.from_addresses) ? tx.from_addresses : [],
      toAddress: String(tx.to_address ?? ""),
      fromExchange: tx.from_exchange || null,
      toExchange: tx.to_exchange || null,
      transferType: tx.transfer_type || "wallet_to_wallet",
      transferDirection: tx.transfer_direction || "neutral",
      transferLabel: tx.transfer_label || "wallet -> wallet",
    }));
  };

  const parseBlocks = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((obj) => {
        const { blockHeight, blockSumAddr, blockSumUTXO, ...rest } = obj || {};
        delete rest.transactions;
        const topAddresses = Object.entries(rest)
          .map(([key, value]) => {
            const address = key.includes("_") ? key.split("_")[1] : key;
            return { address, count: Number(value) };
          })
          .sort((a, b) => b.count - a.count);
        return {
          blockHeight: Number(blockHeight),
          blockSumAddr: Number(blockSumAddr),
          blockSumUTXO: Number(blockSumUTXO),
          transactions: [],
          topAddresses,
        };
      })
      .filter((b) => Number.isFinite(b.blockHeight));
  };

  const parseLargeTxBlocks = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((obj) => ({
        blockHeight: Number(obj?.blockHeight),
        transactions: parseTransactions(obj?.transactions),
      }))
      .filter((b) => Number.isFinite(b.blockHeight));
  };

  const mergeLargeTxBlocks = (utxoBlocks, largeTxBlocks) => {
    const byHeight = new Map((utxoBlocks || []).map((block) => [Number(block.blockHeight), { ...block }]));

    (largeTxBlocks || []).forEach((block) => {
      const existing = byHeight.get(Number(block.blockHeight));
      if (existing) {
        byHeight.set(Number(block.blockHeight), {
          ...existing,
          transactions: block.transactions,
        });
      } else {
        byHeight.set(Number(block.blockHeight), {
          blockHeight: Number(block.blockHeight),
          blockSumAddr: 0,
          blockSumUTXO: 0,
          topAddresses: [],
          transactions: block.transactions,
        });
      }
    });

    return Array.from(byHeight.values()).sort((a, b) => Number(a.blockHeight) - Number(b.blockHeight));
  };

  const fetchAll = async () => {
    setLoading(true);
    setError(false);
    try {
      if (USE_ENV_MOCK) {
        const blocksResp = parseEnvJson(LATEST_UTXO_RESP);
        const largeTxResp = parseEnvJson(LATEST_LARGETX_RESP);
        setBlocks(mergeLargeTxBlocks(
          parseBlocks(blocksResp?.result),
          parseLargeTxBlocks(largeTxResp?.result)
        ));
      } else {
        const [blocksResp, largeTxResp] = await Promise.all([
          retryRequest(() => axios.get(`/api-data?type=latest-utxo`)),
          retryRequest(() => axios.get(`/api-data?type=latest-largetx`)),
        ]);
        setBlocks(mergeLargeTxBlocks(
          parseBlocks(blocksResp.data?.result),
          parseLargeTxBlocks(largeTxResp.data?.result)
        ));
      }
    } catch (e) {
      console.error("Failed to fetch data", e);
      setBlocks([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (blocks && blocks.length) {
      const highestActivityBlock = blocks.reduce((best, block) => {
        const bestValue = Number(best?.blockSumUTXO || 0);
        const currentValue = Number(block?.blockSumUTXO || 0);
        return currentValue > bestValue ? block : best;
      }, blocks[0]);
      setSelectedBlockHeight(highestActivityBlock?.blockHeight ?? blocks[0].blockHeight);
    }
  }, [blocks]);

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="sections-container">
          <section className="dashboard-section top-addresses-section">
            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><span>Loading...</span></div>
            ) : error ? (
              <p className="no-data">Temp busy stay tuned</p>
            ) : blocks.length > 0 ? (
              <TopAddressesPanel blocks={blocks} />
            ) : (
              <p className="no-data">No data</p>
            )}
          </section>

          <section className="dashboard-section chart-section">
            <h2 className="section-header">
              <span><IoFlash /></span> Active Address Per Block
            </h2>
            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><span>Loading...</span></div>
            ) : error ? (
              <p className="no-data">Temp busy stay tuned</p>
            ) : blocks.length > 0 ? (
              <OverviewChart
                blocks={blocks}
                onSelectBlock={(h) => setSelectedBlockHeight(h)}
              />
            ) : (
              <p className="no-data">No data</p>
            )}
          </section>

          <section className="dashboard-section blockcards-section">
            {loading ? (
              <div className="loading-spinner"><div className="spinner"></div><span>Loading...</span></div>
            ) : error ? (
              <p className="no-data">Temp busy stay tuned</p>
            ) : blocks.length > 0 ? (
              <BlockCards blocks={blocks} selectedHeight={selectedBlockHeight} />
            ) : (
              <p className="no-data">No data</p>
            )}
          </section>
        </div>

        <footer className="dashboard-footer">
          <div className="contact-info">
            <div className="contact-links">
              <a
                href="https://x.com/BlockView0214"
                className="contact-link twitter-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="contact-icon"><FaXTwitter /></span>
                Twitter
              </a>
              <a
                href="https://github.com/www222fff/bitcoin_toprank"
                className="contact-link github-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="contact-icon"><FaGithub /></span>
                GitHub
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
